import { QueryClient } from '@tanstack/react-query';

const DEFAULT_OPTIONS = {
  queries: {
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 2,
    refetchOnWindowFocus: true,
    refetchOnReconnect: false,
  },
  mutations: {
    retry: 0,
  },
} as const;

export const createQueryClient = () => new QueryClient({ defaultOptions: DEFAULT_OPTIONS });

export const queryClient = createQueryClient();

export const RECENT_DROPS_LIMIT = 60;

export const queryKeys = {
  profile: ['profile'] as const,
  clickCards: ['click', 'cards'] as const,
  profileStats: (userId?: string) => ['profile', 'stats', userId] as const,
  xpStatus: ['xp', 'status'] as const,
  xpConfig: ['xp', 'config'] as const,
  levelBonuses: ['xp', 'bonuses'] as const,

  chickenTick: ['chicken', 'tick'] as const,

  homeSnapshot: ['home', 'snapshot'] as const,
  comebackBonus: ['home', 'comebackBonus'] as const,
  channels: ['home', 'channels'] as const,
  boostChannels: ['home', 'boostChannels'] as const,
  referralProgress: ['home', 'referralProgress'] as const,
  profileTasks: ['home', 'profileTasks'] as const,
  dailyBonus: ['home', 'dailyBonus'] as const,
  topDrops: ['home', 'topDrops'] as const,

  cases: ['cases'] as const,
  casesCatalog: ['cases', 'catalog'] as const,
  caseDetails: (caseId: string) => ['cases', caseId] as const,

  wikiDrops: (lang: string, entity: string, slug: string) => ['wiki', 'drops', lang, entity, slug] as const,

  inventory: (filter: string) => ['inventory', filter] as const,
  inventoryItem: (itemId: string) => ['inventory', 'item', itemId] as const,
  steamInventory: (userId?: string) => ['inventory', 'steam', userId] as const,

  cs2Matches: ['cs2', 'matches'] as const,

  shopItems: (filters: Record<string, unknown>) => ['shop', 'items', filters] as const,
  shopSkins: (weaponId: string, filters?: Record<string, unknown>) => ['shop', 'skins', weaponId, filters] as const,
  exchangeRate: ['shop', 'exchangeRate'] as const,
  paymentSettings: ['shop', 'paymentSettings'] as const,
  paymentProviders: ['payment', 'providers'] as const,

  upgradeTargets: (source: string, filters: Record<string, unknown>) =>
    ['upgrade', 'targets', source, filters] as const,
  upgradeInventory: (minPrice: number) =>
    ['upgrade', 'inventory', minPrice] as const,
  upgradeHistory: ['upgrade', 'history'] as const,


  draws: ['draws', 'active'] as const,
  drawParticipants: (drawId: string) => ['draws', drawId, 'participants'] as const,
  drawsHistory: (drawType?: string) => ['draws', 'history', drawType ?? 'all'] as const,

  giveawayDetails: (giveawayId: string) => ['giveaway', giveawayId] as const,
  giveawayParticipants: (giveawayId: string) => ['giveaway', giveawayId, 'participants'] as const,

  dailyGiveaways: ['dailyGiveaways'] as const,
  dailyGiveawayDetails: (giveawayId: string) => ['dailyGiveaways', giveawayId] as const,
  dailyGiveawayLeaderboard: (giveawayId: string) => ['dailyGiveaways', giveawayId, 'leaderboard'] as const,
  dailyGiveawayWinners: (giveawayId: string) => ['dailyGiveaways', giveawayId, 'winners'] as const,

  rainCurrent: ['rain', 'current'] as const,
  rainHistory: ['rain', 'history'] as const,
  rainLast: ['rain', 'last'] as const,
  rainDetail: (rainId: string) => ['rain', 'detail', rainId] as const,
  rainParticipants: (rainId: string) => ['rain', 'detail', rainId, 'participants'] as const,

  wheelStatus: ['wheel', 'status'] as const,

  contractsInventory: ['contracts', 'inventory'] as const,
  contractHistory: ['contracts', 'history'] as const,

  referrals: (page?: number) => ['referrals', page] as const,
  referralStats: ['referrals', 'stats'] as const,
  referralContest: ['referrals', 'contest'] as const,

  activeBuff: ['promocode', 'activeBuff'] as const,
  globalBuff: ['promocode', 'globalBuff'] as const,

  caseBattles: ['caseBattles'] as const,
  caseBattleLobby: (filters: unknown) => ['caseBattles', 'lobby', filters] as const,
  caseBattleDetails: (battleId: string) => ['caseBattles', battleId] as const,
  caseBattleViewers: (battleId: string) => ['caseBattles', battleId, 'viewers'] as const,
  caseBattleHistoryAll: ['caseBattles', 'history', 'all'] as const,
  caseBattleHistoryMine: ['caseBattles', 'history', 'mine'] as const,
  userActiveBattlesCount: ['caseBattles', 'userActiveCount'] as const,
  caseBattleConfig: ['caseBattles', 'config'] as const,
  caseBattleTemplates: ['caseBattles', 'templates'] as const,

  rushMidHistory: ['rushMid', 'history'] as const,
  rushMidRating: (mode: number) => ['rushMid', 'rating', mode] as const,
  rushMidSkins: ['rushMid', 'skins'] as const,
  rushMidOpenGames: ['rushMid', 'openGames'] as const,
  rushMidOpenGame: (matchId: string) => ['rushMid', 'openGame', matchId] as const,
  rushMidLiveGames: ['rushMid', 'liveGames'] as const,

  rouletteGames: ['roulette', 'games'] as const,
  rouletteHistory: ['roulette', 'history'] as const,
  rouletteFeed: ['roulette', 'feed'] as const,
  rouletteState: ['roulette', 'state'] as const,
  rouletteMatch: (matchId: string) => ['roulette', 'match', matchId] as const,
  rouletteFairness: (matchId: string) => ['roulette', 'fairness', matchId] as const,

  rushRaffleRounds: ['rushRaffle', 'rounds'] as const,
  rushRaffleRoundDetail: (roundId: string | null) => ['rushRaffle', 'round', roundId] as const,
  rushRaffleMatchAward: (matchId: string | null) => ['rushRaffle', 'matchAward', matchId] as const,
  rushRaffleWinners: ['rushRaffle', 'winners'] as const,

  onlineCount: ['realtime', 'online'] as const,

  achievementsCatalog: ['achievements', 'catalog'] as const,
  achievementsMine: ['achievements', 'mine'] as const,
  achievementsUser: (userId?: string) => ['achievements', 'user', userId] as const,
  achievementsStats: (key?: string) => ['achievements', 'stats', key] as const,
  achievementsRecentReceivers: (key?: string) => ['achievements', 'recent-receivers', key] as const,

  bandStatus: ['band', 'status'] as const,
  bandQuests: ['band', 'quests'] as const,
  bandFreeCases: ['band', 'free-cases'] as const,
  bandMembersPreview: ['band', 'members-preview'] as const,
  bandRaceDetails: ['band', 'race', 'details'] as const,
  bandRaceLeaderboard: ['band', 'race', 'leaderboard'] as const,
  bandRaceWinners: ['band', 'race', 'winners'] as const,
  bandFinal: ['band', 'final'] as const,
  bandFinalRound: ['band', 'final', 'round'] as const,
  bandFinalWinners: ['band', 'final', 'winners'] as const,
  bandFinalBattleInfo: (battleId: string) => ['band', 'final', 'battle', battleId] as const,
  bandRaffleRound: ['band', 'raffle', 'round'] as const,
  bandRaffleRoundDetail: (roundId: string | null) => ['band', 'raffle', 'round', roundId] as const,
  bandRaffleWinners: ['band', 'raffle', 'winners'] as const,

  supportSummary: ['support', 'summary'] as const,
  supportMessagesTail: ['support', 'messages', 'tail'] as const,
} as const;
